import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Store-pickup alerts on the ops dashboard.
 *
 * Polls the store-pickup board every 20 seconds and raises an alert - a toast, a sound, and a desktop
 * notification when the browser allows it - when:
 *   - a NEW order starts waiting at a store, and
 *   - an order has waited at a store LONGER than the chosen threshold without being collected.
 *
 * Each order alerts at most once per kind, remembered in this browser (localStorage), so a reload or
 * a threshold change does not replay alerts. The first load in a browser only takes a baseline:
 * what is already waiting (or already overdue) is shown in the panel, not announced.
 */
const POLL_MS = 20000;
const BOARD_PATH = '/orders/store-pickups';
const KEY_SEEN = 'spAlerts.seen.v1';
const KEY_OVERDUE = 'spAlerts.overdue.v2';
const KEY_THRESHOLD = 'spAlerts.overdueMins';
const KEY_MUTED = 'spAlerts.muted';
const KEEP_MS = 3 * 24 * 3600 * 1000;
const THRESHOLDS = [15, 30, 45, 60, 90];

// Storage can be blocked (private mode, policies); alerts still work for this page view without it.
const readJson = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : JSON.parse(v);
  } catch (e) {
    return fallback;
  }
};
const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* not persisted - fine */
  }
};
const prune = (map) => {
  const cutoff = Date.now() - KEEP_MS;
  const out = {};
  Object.entries(map || {}).forEach(([k, t]) => {
    if (t >= cutoff) out[k] = t;
  });
  return out;
};

// One shared AudioContext. Browsers only let audio start after a user gesture, so the first click
// anywhere on the page unlocks it; until then an alert is silent but still shows.
let audioCtx = null;
const getAudio = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx) audioCtx = new Ctx();
  if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
  return audioCtx;
};
const beep = (pattern) => {
  try {
    const ctx = getAudio();
    if (!ctx) return;
    let t = ctx.currentTime;
    pattern.forEach(([freq, dur]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + dur + 0.02);
      t += dur + 0.08;
    });
  } catch (e) {
    /* sound is a nicety */
  }
};
const SOUND_NEW = [[880, 0.15], [1175, 0.2]];
const SOUND_OVERDUE = [[660, 0.25], [520, 0.25], [660, 0.25]];

// 211h 28m reads badly; past a day show days and hours.
const fmtAge = (mins) => {
  if (mins == null) return '—';
  if (mins >= 1440) return `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`;
  if (mins >= 60) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${mins}m`;
};
const ref = (r) => r.businessRefNo || r.jobId || '—';
const label = (r) => `${r.store || 'Store'} · ${ref(r)}`;
const MOVE_SHORT = { STORE_TO_STORE: 'Store → store', STORE_TO_CUSTOMER: 'To customer', TO_STORE: 'Into store' };

const CSS = `
  .spa { background:#fff; border:1px solid #e2e8f0; border-radius:16px; box-shadow:0 2px 14px -6px rgba(15,23,42,.14);
         padding:18px 20px; text-align:left; color:#0f172a; }
  .spa-head { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; }
  .spa-title { display:flex; align-items:center; gap:12px; min-width:0; }
  .spa-icon { width:40px; height:40px; border-radius:12px; background:#eff6ff; color:#2563eb; display:flex;
              align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .spa-title h3 { margin:0; font-size:16px; font-weight:700; color:#1e293b; line-height:1.25; }
  .spa-sub { font-size:12px; color:#64748b; margin-top:3px; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
  .spa-dot { width:8px; height:8px; border-radius:50%; background:#16a34a; box-shadow:0 0 0 3px rgba(22,163,74,.16); }
  .spa-dot.err { background:#dc2626; box-shadow:0 0 0 3px rgba(220,38,38,.16); }
  .spa-controls { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .spa-ctl { display:inline-flex; align-items:center; gap:6px; height:34px; padding:0 12px; border:1px solid #e2e8f0;
             border-radius:10px; background:#fff; font-size:12.5px; color:#334155; cursor:pointer; white-space:nowrap;
             font-family:inherit; }
  .spa-ctl select { border:none; background:transparent; font-size:12.5px; font-weight:700; color:#0f172a; outline:none;
                    cursor:pointer; font-family:inherit; padding:0; }
  .spa-ctl.primary { background:#1e3a8a; border-color:#1e3a8a; color:#fff; font-weight:600; text-decoration:none; }
  .spa-ctl.primary:hover { background:#1e40af; }
  .spa-stats { display:grid; grid-template-columns:repeat(3, minmax(0, 1fr)); gap:12px; margin-top:16px; }
  .spa-stat { border-radius:12px; padding:12px 14px; display:flex; align-items:center; gap:12px; min-width:0; }
  .spa-stat-icon { width:36px; height:36px; border-radius:10px; display:flex; align-items:center; justify-content:center;
                   font-size:15px; flex-shrink:0; background:rgba(255,255,255,.75); }
  .spa-stat-val { font-size:24px; font-weight:800; line-height:1; font-variant-numeric:tabular-nums; }
  .spa-stat-lbl { font-size:12px; font-weight:600; margin-top:4px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .spa-body { display:grid; grid-template-columns:minmax(0, 1.6fr) minmax(0, 1fr); gap:16px; margin-top:16px; }
  .spa-box { border:1px solid #eef2f7; border-radius:12px; overflow:hidden; min-width:0; background:#fff; }
  .spa-box-h { display:flex; justify-content:space-between; align-items:center; gap:8px; padding:10px 14px;
               background:#f8fafc; border-bottom:1px solid #eef2f7; font-size:11.5px; font-weight:700; color:#475569;
               text-transform:uppercase; letter-spacing:.05em; }
  .spa-count { background:#e2e8f0; color:#334155; border-radius:999px; padding:1px 8px; font-size:11px; }
  .spa-count.red { background:#fee2e2; color:#b91c1c; }
  .spa-list { max-height:248px; overflow-y:auto; }
  .spa-row { display:grid; grid-template-columns:minmax(0, 1fr) auto auto; gap:12px; align-items:center;
             padding:9px 14px; border-bottom:1px solid #f1f5f9; }
  .spa-row:last-child, .spa-alert:last-child { border-bottom:none; }
  .spa-ref { font-size:13px; font-weight:600; color:#0f172a; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .spa-meta { font-size:11.5px; color:#64748b; margin-top:1px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .spa-chip { font-size:11px; font-weight:600; color:#475569; background:#f1f5f9; border-radius:999px; padding:3px 9px;
              white-space:nowrap; }
  .spa-age { font-size:13px; font-weight:700; color:#b91c1c; white-space:nowrap; font-variant-numeric:tabular-nums;
             min-width:56px; text-align:right; }
  .spa-empty { padding:22px 14px; text-align:center; font-size:13px; color:#64748b; }
  .spa-alert { display:grid; grid-template-columns:auto minmax(0, 1fr); gap:10px; padding:9px 14px;
               border-bottom:1px solid #f1f5f9; align-items:start; }
  .spa-alert-time { font-size:11.5px; color:#94a3b8; white-space:nowrap; font-variant-numeric:tabular-nums; padding-top:1px; }
  .spa-alert-title { font-size:12.5px; font-weight:700; }
  .spa-err { margin-top:12px; font-size:12.5px; color:#b91c1c; }
  .spa-toasts { position:fixed; top:16px; right:16px; z-index:2000; display:flex; flex-direction:column; gap:10px;
                width:340px; max-width:calc(100vw - 32px); }
  .spa-toast { cursor:pointer; background:#fff; border-radius:12px; box-shadow:0 12px 32px rgba(15,23,42,.22);
               padding:12px 14px; text-align:left; }
  @media (max-width: 1100px) {
    .spa-body { grid-template-columns:1fr; }
  }
  @media (max-width: 720px) {
    .spa { padding:14px; border-radius:14px; }
    .spa-controls { width:100%; }
    .spa-ctl.primary { margin-left:auto; }
    .spa-stats { gap:8px; }
    .spa-stat { flex-direction:column; align-items:flex-start; gap:6px; padding:10px 12px; }
    .spa-stat-icon { display:none; }
    .spa-stat-val { font-size:20px; }
    .spa-stat-lbl { white-space:normal; line-height:1.25; }
    .spa-row { grid-template-columns:minmax(0, 1fr) auto; }
    .spa-row .spa-chip { display:none; }
  }
`;

export default function StorePickupAlerts() {
  const API_URL = process.env.REACT_APP_API_URL;

  const [waiting, setWaiting] = useState([]);
  const [pickedToday, setPickedToday] = useState(0);
  const [log, setLog] = useState([]); // this page view's alerts, newest first
  const [toasts, setToasts] = useState([]);
  const [threshold, setThreshold] = useState(() => {
    const v = Number(readJson(KEY_THRESHOLD, 30));
    return THRESHOLDS.includes(v) ? v : 30;
  });
  const [muted, setMuted] = useState(() => readJson(KEY_MUTED, false) === true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState('');
  const [notifPerm, setNotifPerm] = useState(() =>
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
  );

  // The poll runs on an interval, so it reads the live settings through refs, not a stale closure.
  const thresholdRef = useRef(threshold);
  const mutedRef = useRef(muted);
  useEffect(() => {
    thresholdRef.current = threshold;
    writeJson(KEY_THRESHOLD, threshold);
  }, [threshold]);
  useEffect(() => {
    mutedRef.current = muted;
    writeJson(KEY_MUTED, muted);
  }, [muted]);

  useEffect(() => {
    const unlock = () => {
      try {
        getAudio();
      } catch (e) {
        /* ignore */
      }
    };
    window.addEventListener('click', unlock, { once: true });
    return () => window.removeEventListener('click', unlock);
  }, []);

  const dismiss = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const raise = useCallback((kind, list) => {
    const mins = thresholdRef.current;
    const title =
      kind === 'new'
        ? list.length === 1 ? 'New store pickup' : `${list.length} new store pickups`
        : list.length === 1 ? `Waiting over ${mins} min` : `${list.length} orders waiting over ${mins} min`;
    const lines = list
      .slice(0, 3)
      .map((r) => (kind === 'overdue' ? `${label(r)} · ${fmtAge(r.waitingMinutes)}` : label(r)));
    if (list.length > 3) lines.push(`+${list.length - 3} more`);
    const body = lines.join('\n');
    const id = `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    setLog((prev) => [{ id, kind, title, body, at: new Date() }, ...prev].slice(0, 20));
    setToasts((prev) => [{ id, kind, title, body }, ...prev].slice(0, 4));
    setTimeout(() => dismiss(id), 12000);
    if (!mutedRef.current) beep(kind === 'new' ? SOUND_NEW : SOUND_OVERDUE);
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
        const n = new Notification(title, { body, tag: id });
        n.onclick = () => {
          window.focus();
          window.location.href = BOARD_PATH;
        };
      }
    } catch (e) {
      /* desktop notification is optional */
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}shypvdriverapp/api/store-pickups`, {
        params: { includeCollected: true },
      });
      const data = res?.data;
      if (data && data.success === false) {
        const first = (data.errors || [])[0];
        setError(first?.errorDetails || first?.errorMessage || 'Could not load store pickups');
        return;
      }
      const all = Array.isArray(data?.data) ? data.data : [];
      const now = all.filter((r) => r.stage !== 'PICKED_UP');
      setWaiting(now);
      setPickedToday(all.length - now.length);
      setLastUpdated(new Date());
      setError('');

      const ts = Date.now();

      // New arrivals. No stored map = this browser has never polled: baseline, announce nothing.
      const storedSeen = readJson(KEY_SEEN, null);
      const seen = prune(storedSeen || {});
      const fresh = storedSeen === null ? [] : now.filter((r) => r.jobId && !seen[r.jobId]);
      now.forEach((r) => {
        if (r.jobId && !seen[r.jobId]) seen[r.jobId] = ts;
      });
      writeJson(KEY_SEEN, seen);

      // Waiting too long: once per order, whatever the threshold - changing it must not replay alerts.
      // Same baseline rule, so opening the dashboard does not announce what is already listed below.
      const storedOverdue = readJson(KEY_OVERDUE, null);
      const alerted = prune(storedOverdue || {});
      const limit = thresholdRef.current;
      const crossed = now.filter((r) => r.jobId && r.waitingMinutes != null && r.waitingMinutes >= limit && !alerted[r.jobId]);
      crossed.forEach((r) => {
        alerted[r.jobId] = ts;
      });
      writeJson(KEY_OVERDUE, alerted);
      const overdue = storedOverdue === null ? [] : crossed;

      if (fresh.length) raise('new', fresh);
      if (overdue.length) raise('overdue', overdue);
    } catch (e) {
      setError(e?.response?.status === 404 ? 'Store pickups are not available on this server' : e?.message || 'Could not reach the driver backend');
    }
  }, [API_URL, raise]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [poll]);

  const overdueNow = waiting
    .filter((r) => r.waitingMinutes != null && r.waitingMinutes >= threshold)
    .sort((a, b) => b.waitingMinutes - a.waitingMinutes);

  const enableDesktop = async () => {
    try {
      const p = await Notification.requestPermission();
      setNotifPerm(p);
    } catch (e) {
      /* unsupported */
    }
  };

  const stat = (icon, value, text, color, bg) => (
    <div className="spa-stat" style={{ background: bg, color }}>
      <div className="spa-stat-icon"><i className={`fa ${icon}`} /></div>
      <div style={{ minWidth: 0 }}>
        <div className="spa-stat-val">{value}</div>
        <div className="spa-stat-lbl">{text}</div>
      </div>
    </div>
  );

  return (
    <>
      <style>{CSS}</style>

      {/* Toasts: top-right, newest first, click to open the board. */}
      <div className="spa-toasts">
        {toasts.map((t) => (
          <div key={t.id} role="alert" className="spa-toast"
            onClick={() => { window.location.href = BOARD_PATH; }}
            style={{ borderLeft: `5px solid ${t.kind === 'new' ? '#2563eb' : '#dc2626'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <strong style={{ fontSize: 14, color: t.kind === 'new' ? '#1e3a8a' : '#b91c1c' }}>
                <i className={`fa ${t.kind === 'new' ? 'fa-shopping-bag' : 'fa-clock-o'}`} style={{ marginRight: 6 }} />
                {t.title}
              </strong>
              <button aria-label="Dismiss" onClick={(e) => { e.stopPropagation(); dismiss(t.id); }}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1 }}>
                ×
              </button>
            </div>
            <div style={{ whiteSpace: 'pre-line', fontSize: 12.5, color: '#334155', marginTop: 4 }}>{t.body}</div>
          </div>
        ))}
      </div>

      <section className="spa" aria-label="Store pickup alerts">
        <div className="spa-head">
          <div className="spa-title">
            <div className="spa-icon"><i className="fa fa-bell" /></div>
            <div style={{ minWidth: 0 }}>
              <h3>Store pickup alerts</h3>
              <div className="spa-sub">
                <span className={`spa-dot${error ? ' err' : ''}`} />
                <span>Live · refreshes every {POLL_MS / 1000}s{lastUpdated ? ` · ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}</span>
              </div>
            </div>
          </div>
          <div className="spa-controls">
            <label className="spa-ctl" title="Alert when an order has waited at the store longer than this">
              <i className="fa fa-clock-o" style={{ color: '#64748b' }} />
              Alert after
              <select value={threshold} onChange={(e) => setThreshold(Number(e.target.value))}>
                {THRESHOLDS.map((m) => <option key={m} value={m}>{m} min</option>)}
              </select>
            </label>
            <button type="button" className="spa-ctl" onClick={() => setMuted((m) => !m)} title={muted ? 'Turn alert sound on' : 'Turn alert sound off'}>
              <i className={`fa ${muted ? 'fa-volume-off' : 'fa-volume-up'}`} />
              {muted ? 'Sound off' : 'Sound on'}
            </button>
            {notifPerm === 'default' && (
              <button type="button" className="spa-ctl" onClick={enableDesktop} title="Show alerts even when this tab is in the background">
                <i className="fa fa-desktop" />Desktop alerts
              </button>
            )}
            <a href={BOARD_PATH} className="spa-ctl primary">Open board <i className="fa fa-arrow-right" /></a>
          </div>
        </div>

        <div className="spa-stats">
          {stat('fa-shopping-bag', waiting.length, 'Waiting now', '#1e3a8a', '#eef2ff')}
          {stat('fa-clock-o', overdueNow.length, `Waiting over ${threshold} min`,
            overdueNow.length ? '#b91c1c' : '#475569', overdueNow.length ? '#fee2e2' : '#f1f5f9')}
          {stat('fa-check-circle', pickedToday, 'Picked up today', '#166534', '#dcfce7')}
        </div>

        <div className="spa-body">
          <div className="spa-box">
            <div className="spa-box-h">
              <span>Waiting too long</span>
              <span className={`spa-count${overdueNow.length ? ' red' : ''}`}>{overdueNow.length}</span>
            </div>
            {overdueNow.length === 0 ? (
              <div className="spa-empty"><i className="fa fa-check" style={{ color: '#16a34a', marginRight: 6 }} />Nothing has waited over {threshold} min</div>
            ) : (
              <div className="spa-list">
                {overdueNow.map((r) => (
                  <div key={r.jobId} className="spa-row">
                    <div style={{ minWidth: 0 }}>
                      <div className="spa-ref" title={ref(r)}>{ref(r)}</div>
                      <div className="spa-meta">{r.store || 'Store'}{r.driverName ? ` · ${r.driverName}` : ' · Unassigned'}</div>
                    </div>
                    <span className="spa-chip">{MOVE_SHORT[r.movementType] || r.movementLabel || '—'}</span>
                    <span className="spa-age">{fmtAge(r.waitingMinutes)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="spa-box">
            <div className="spa-box-h">
              <span>Recent alerts</span>
              <span className="spa-count">{log.length}</span>
            </div>
            {log.length === 0 ? (
              <div className="spa-empty">No new alerts since you opened the dashboard</div>
            ) : (
              <div className="spa-list">
                {log.map((a) => (
                  <div key={a.id} className="spa-alert">
                    <span className="spa-alert-time">{a.at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    <div style={{ minWidth: 0 }}>
                      <div className="spa-alert-title" style={{ color: a.kind === 'new' ? '#1e3a8a' : '#b91c1c' }}>{a.title}</div>
                      <div className="spa-meta" title={a.body}>{a.body.split('\n')[0]}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {error && <div className="spa-err"><i className="fa fa-exclamation-triangle" style={{ marginRight: 6 }} />{error}</div>}
      </section>
    </>
  );
}
